<template>
  <div class="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-p-6 tw-shadow-sm">
    <div class="tw-flex tw-items-center tw-gap-3 tw-mb-4">
      <div class="tw-w-10 tw-h-10 tw-bg-primary-100 tw-rounded-lg tw-flex tw-items-center tw-justify-center">
        <icon icon="solar:info-circle-bold" class="tw-text-primary-600" />
      </div>
      <h3 class="tw-text-lg tw-font-semibold tw-text-gray-800">Session Information</h3>
    </div>
    
    <div class="tw-space-y-4">
      <div class="tw-flex tw-items-center tw-justify-between tw-p-3 tw-bg-gray-50 tw-rounded-lg">
        <span class="tw-text-sm tw-text-gray-600">Start Date</span>
        <span class="tw-font-semibold tw-text-gray-800">{{ formattedSessionDate }}</span>
      </div>
      
      <div class="tw-flex tw-items-center tw-justify-between tw-p-3 tw-bg-gray-50 tw-rounded-lg">
        <span class="tw-text-sm tw-text-gray-600">Duration</span>
        <span class="tw-font-semibold tw-text-gray-800">{{ formattedDuration }}</span>
      </div>
      
      <div v-if="session?.attendees_count" class="tw-flex tw-items-center tw-justify-between tw-p-3 tw-bg-gray-50 tw-rounded-lg">
        <span class="tw-text-sm tw-text-gray-600">Expected Attendees</span>
        <span class="tw-font-semibold tw-text-gray-800">{{ session.attendees_count }}</span>
      </div>
      
      <div v-if="session?.speakers && session.speakers.length > 0" class="tw-flex tw-items-center tw-justify-between tw-p-3 tw-bg-gray-50 tw-rounded-lg">
        <span class="tw-text-sm tw-text-gray-600">Speakers</span>
        <span class="tw-font-semibold tw-text-gray-800">{{ session.speakers.length }}</span>
      </div>
    </div>

    <!-- Join/Leave Session Buttons -->
    <div class="tw-mt-6 tw-pt-4 tw-border-t tw-border-gray-200">
      <div class="tw-text-center tw-mb-4">
        <h4 class="tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">Session Registration</h4>
        <p class="tw-text-xs tw-text-gray-500">Join this session to receive updates and participate in discussions</p>
      </div>
      
      <!-- Speaker Message -->
      <div v-if="isCurrentUserSpeaker" class="tw-text-center tw-mb-4">
        <div class="tw-inline-flex tw-items-center tw-gap-2 tw-bg-blue-100 tw-text-blue-800 tw-text-xs tw-font-medium tw-px-3 tw-py-2 tw-rounded-full">
          <icon icon="solar:user-bold" class="tw-text-blue-600" />
          You're a speaker for this session
        </div>
      </div>
      
      <!-- Join/Leave Buttons (hidden for speakers and unauthenticated users) -->
      <div v-else-if="isAuthenticated" class="tw-space-y-3">
        <!-- Join Button -->
        <a-button 
          v-if="!session?.is_joined" 
          @click="handleJoinSession"
          :disabled="joinLoading"
          intent="primary"
          size="lg"
          class="tw-w-full"
        >
        <div class="tw-flex tw-items-center tw-justify-center">
          <icon v-if="joinLoading" icon="solar:loading-bold" class="tw-animate-spin tw-mr-2" />
          <icon v-else icon="solar:plus-circle-bold" class="tw-mr-2" />
          {{ joinLoading ? 'Joining...' : 'Join Session' }}
        </div>
        </a-button>
        
        <!-- Leave Button -->
        <a-button 
          v-else 
          @click="handleLeaveSession"
          :disabled="leaveLoading"
          intent="secondary"
          size="lg"
          class="tw-w-full tw-text-red-600 tw-border-red-200 hover:tw-border-red-300 hover:tw-bg-red-50"
        >
          <div class="tw-flex tw-items-center tw-justify-center">
            <icon v-if="leaveLoading" icon="solar:loading-bold" class="tw-animate-spin tw-mr-2" />
            <icon v-else icon="solar:minus-circle-bold" class="tw-mr-2" />
            {{ leaveLoading ? 'Leaving...' : 'Leave Session' }}
          </div>
        </a-button>
      </div>
      
      <!-- Authentication Required Message -->
      <div v-else class="tw-text-center tw-mb-4">
        <div class="tw-inline-flex tw-items-center tw-gap-2 tw-bg-gray-100 tw-text-gray-600 tw-text-xs tw-font-medium tw-px-3 tw-py-2 tw-rounded-full">
          <icon icon="solar:lock-bold" class="tw-text-gray-500" />
          Please log in to join this session
        </div>
      </div>
      
      <!-- Join Status Indicator -->
      <div v-if="session?.is_joined" class="tw-mt-3 tw-text-center">
        <div class="tw-inline-flex tw-items-center tw-gap-2 tw-bg-green-100 tw-text-green-800 tw-text-xs tw-font-medium tw-px-3 tw-py-1.5 tw-rounded-full">
          <icon icon="solar:check-circle-bold" class="tw-text-green-600" />
          You're registered for this session
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, defineProps, defineEmits } from 'vue'
import moment from 'moment'
import { join } from '@/api/sessions'
import useAlert from '@/composables/useAlert'

const props = defineProps({
  session: {
    type: Object,
    required: true
  },
  isCurrentUserSpeaker: {
    type: Boolean,
    default: false
  },
  isAuthenticated: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['session-joined', 'session-left', 'login-required'])

// Join/Leave session loading states
const joinLoading = ref(false)
const leaveLoading = ref(false)

// Computed properties for formatting
const formattedSessionDate = computed(() => {
  if (!props.session?.start_date) return 'Date not specified'
  
  try {
    const dateObj = moment(props.session.start_date)
    const today = moment()
    const tomorrow = moment().add(1, 'day')

    if (dateObj.isSame(today, 'day')) {
      return `Today ${dateObj.format('h:mm A')}`
    } else if (dateObj.isSame(tomorrow, 'day')) {
      return `Tomorrow ${dateObj.format('h:mm A')}`
    } else {
      return dateObj.format('MMM D, h:mm A')
    }
  } catch (error) {
    console.error('Error formatting session date:', error)
    return 'Date not specified'
  }
})

const formattedDuration = computed(() => {
  if (!props.session?.start_date || !props.session?.end_date) return 'Duration not specified'
  
  try {
    const start = moment(props.session.start_date)
    const end = moment(props.session.end_date)
    const duration = moment.duration(end.diff(start))
    const hours = Math.floor(duration.asHours())
    const minutes = duration.minutes()
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`
    }
    return `${minutes}m`
  } catch (error) {
    console.error('Error calculating duration:', error)
    return 'Duration not specified'
  }
})

// Handle joining/leaving a session (using the toggle API like other components)
const handleJoinSession = async () => {
  if (!props.session?.id) {
    useAlert('Session ID not found', 'error')
    return
  }

  joinLoading.value = true
  
  try {
    // Call the join API (this is a toggle endpoint)
    await join(props.session.id)
    
    const action = props.session.is_joined ? 'left' : 'joined'
    useAlert(`Successfully ${action} the session!`, 'success')
    emit('session-joined', props.session.id)
  } catch (error) {
    console.error('Error joining session:', error)
    
    // Handle specific error cases
    if (error.response?.status === 422) {
      const errorMessage = error.response.data?.message || 'This session has already ended.'
      useAlert(errorMessage, 'warning')
    } else {
      useAlert('Failed to join session. Please try again.', 'error')
    }
  } finally {
    joinLoading.value = false
  }
}

// Handle leaving a session (using the same toggle API)
const handleLeaveSession = async () => {
  if (!props.session?.id) {
    useAlert('Session ID not found', 'error')
    return
  }

  leaveLoading.value = true
  
  try {
    // Call the join API (this is a toggle endpoint that handles both join and leave)
    await join(props.session.id)
    
    const action = props.session.is_joined ? 'left' : 'joined'
    useAlert(`Successfully ${action} the session!`, 'success')
    emit('session-left', props.session.id)
  } catch (error) {
    console.error('Error leaving session:', error)
    
    // Handle specific error cases
    if (error.response?.status === 422) {
      const errorMessage = error.response.data?.message || 'Cannot leave this session. It may have already started.'
      useAlert(errorMessage, 'warning')
    } else {
      useAlert('Failed to leave session. Please try again.', 'error')
    }
  } finally {
    leaveLoading.value = false
  }
}
</script>
